// M-122: 初回診断アンケート（`docs/ONBOARDING-SURVEY-DESIGN.md`）の型と純関数。
//
// 設計の要:
//
// - **診断は「1 枚の巨大アンケート」ではなく、既存の `promo_surveys` 4 doc への
//   ファンアウト**として実装する（inspection / washpass / keeper / car_sales）。
//   こうすると `recordSurveyAnswer` → `applySurveyAnswer` → `applyPromoEvent` の既存経路が
//   そのまま効き、「最新回答で上書き / 確度は単調 / ステージは昇格のみ」（設計書 §7-9）は
//   **新規実装ゼロ**で成立する。
// - 選択肢 → 確度の紐付けは既存の `PromoSurveyOption.interestChoice` で表現する。
//   ⚠ **`interestChoice` を付けない選択肢は「ステージ変化なし」**になる
//   （`stageGrantedBySurveyChoice(undefined)` = 'attention' = 付与なし）。設計書 §2-4 の
//   「このフォームから ni は一切発生させない」は、**`not_interested` を 1 つも使わない**
//   ことで満たす（`not_interested` は `isInterestOptedOut` = 商材の配信停止を意味する）。
// - 文面・クーポン参照・通知先・サイクル値は**設定 doc 駆動**（設計書 §10.2）。
//   本ファイルが持つのは**マッピング（KGI 機械の配線）だけ**で、こちらは画面から編集不可。
import { PROMO_SURVEY_INTEREST_KEYS } from './promoTypes.js';
/** 診断の設問ブロック（= ファンアウト先の商材）。順序は設計書 §3 の出題順。 */
export const DIAGNOSIS_BLOCK_KEYS = [
    'inspection',
    'washpass',
    'keeper',
    'car_sales',
];
/**
 * ブロックキーは**アンケート対象業態の部分集合**でなければならない。
 * `fuel` はアンケート対象外（`PROMO_SURVEY_INTEREST_KEYS` に無い）ため、
 * 診断ブロックにも置けない — クーポンの**付与先**としてのみ登場する。
 */
export function isDiagnosisBlockKey(value) {
    return DIAGNOSIS_BLOCK_KEYS.includes(value);
}
/** 診断ブロック → 商材キー（同名だが、型として明示的に橋渡しする）。 */
export function diagnosisBlockInterestKey(key) {
    // DIAGNOSIS_BLOCK_KEYS ⊂ PROMO_SURVEY_INTEREST_KEYS を型でなく値で保証する。
    const found = PROMO_SURVEY_INTEREST_KEYS.find((k) => k === key);
    if (!found) {
        throw new Error(`診断ブロック "${key}" はアンケート対象業態ではありません`);
    }
    return found;
}
/**
 * 診断の設問とマッピング（**固定 / 画面から編集不可**）。
 *
 * 文言は既定値であり、実際の表示は `promo_surveys` doc（管理画面で編集）が優先する。
 * ここで固定するのは **選択肢 id → 確度（→ ステージ）** の対応だけで、これは
 * 設計書 §10.2 の「固定（表示のみ）」に当たる。
 */
export const DIAGNOSIS_BLOCKS = [
    {
        key: 'inspection',
        title: 'Q1 次の車検はいつ頃ですか？',
        questionText: '次の車検はいつ頃ですか？',
        options: [
            {
                id: 'within_3m',
                label: '3 ヶ月以内',
                interestChoice: 'interested_planned',
                note: '欲求（D）＋サンキューで予約導線と車検クーポン',
            },
            {
                id: 'within_6m',
                label: '半年以内',
                interestChoice: 'slightly_interested',
                note: '興味（I）',
            },
            {
                id: 'later',
                label: '半年より先',
                note: 'ステージ変化なし（確度 self_declared のみ）',
            },
            {
                id: 'unknown',
                label: 'わからない',
                note: 'ステージ変化なし（検査標章の見方を案内）',
            },
        ],
        monthQuestion: {
            id: 'inspection_due_month',
            text: '車検満了の年月（車検証・検査標章に記載 / 任意）',
        },
    },
    {
        key: 'washpass',
        title: 'Q2 洗車の頻度は？',
        questionText: '洗車の頻度は？',
        options: [
            {
                id: 'weekly',
                label: '週 1 回以上',
                interestChoice: 'slightly_interested',
                note: '興味（I）＋ WashPass 訴求（強）',
            },
            {
                id: 'monthly',
                label: '月 1〜2 回',
                interestChoice: 'slightly_interested',
                note: '興味（I）＋ WashPass 訴求（弱）',
            },
            { id: 'rarely', label: 'ほとんどしない', note: 'ステージ変化なし' },
        ],
    },
    {
        key: 'keeper',
        title: 'Q3 ボディコーティングに興味はありますか？',
        questionText: 'ボディコーティングに興味はありますか？',
        options: [
            {
                id: 'interested',
                label: '興味あり',
                interestChoice: 'slightly_interested',
                note: '興味（I）＋ KeePer 体験クーポン',
            },
            {
                id: 'done_before',
                label: '施工したことがある',
                interestChoice: 'slightly_interested',
                note: '興味（I）',
            },
            { id: 'none', label: '特にない', note: 'ステージ変化なし' },
        ],
    },
    {
        key: 'car_sales',
        title: 'Q4 お車の買い替えについて（任意）',
        questionText: 'お車の買い替えについて',
        optional: true,
        options: [
            {
                id: 'within_1y',
                label: '1 年以内に買い替えたい',
                interestChoice: 'interested_planned',
                note: '欲求（D）＋ HOT 起票（担当者通知）',
            },
            {
                id: 'if_good_one',
                label: '良い車があれば／情報収集中',
                interestChoice: 'slightly_interested',
                note: '興味（I）',
            },
            {
                id: 'no_plan',
                label: '予定なし',
                note: 'ステージ変化なし（**ni にしない**）',
            },
        ],
    },
];
/** ブロックキーで引く（存在しないキーは undefined）。 */
export function findDiagnosisBlock(key) {
    return DIAGNOSIS_BLOCKS.find((b) => b.key === key);
}
/**
 * 設定が無いときの必須ブロック（= `optional` でないもの）。
 *
 * **定義から導出する** — 配列を書き写すと、`DIAGNOSIS_BLOCKS` 側で任意設問が増えたときに
 * 「必須のまま」になり、顧客が送信できない画面が黙って出来上がる。
 */
export const DEFAULT_REQUIRED_DIAGNOSIS_BLOCK_KEYS = DIAGNOSIS_BLOCKS.filter((b) => b.optional !== true).map((b) => b.key);
/**
 * 実効の必須ブロックを解決する（**顧客フォーム / サーバ検証 / 管理画面の唯一の入口**）。
 *
 * - `undefined`（未設定）→ コード既定（`DEFAULT_REQUIRED_DIAGNOSIS_BLOCK_KEYS`）
 * - 配列 → その内容。**空配列は「全問任意」という意味を持つ明示値**として通す
 *   （`?? []` のような書き方で潰さないこと）
 *
 * ⚠ 3 者が別々に導出すると「画面は必須と言うのにサーバは通す」「顧客は送信できるのに
 * サーバが弾く」が無言で起きる。増やすときは必ずこの関数を経由させる。
 *
 * 未知のキー（設定 doc に古いブロック名が残っている等）は落とし、**出題順に正規化**して返す
 * （保存順に引きずられると、必須の並びが画面とサーバで食い違って見える）。
 */
export function resolveRequiredDiagnosisBlocks(requiredBlocks) {
    if (requiredBlocks === undefined || requiredBlocks === null) {
        return DEFAULT_REQUIRED_DIAGNOSIS_BLOCK_KEYS;
    }
    const wanted = new Set(requiredBlocks.filter(isDiagnosisBlockKey));
    return DIAGNOSIS_BLOCK_KEYS.filter((key) => wanted.has(key));
}
/** 選択肢 id → 確度（未紐付けは undefined = ステージ変化なし）。 */
export function diagnosisOptionChoice(blockKey, optionId) {
    return findDiagnosisBlock(blockKey)?.options.find((o) => o.id === optionId)
        ?.interestChoice;
}
// ---------------------------------------------------------------------------
// サンキュー分岐（設計書 §4）
// ---------------------------------------------------------------------------
/**
 * サンキュー 1 通に載せる条件ブロックの種類。
 *
 * 燃料クーポン（お礼＋全員固定）はブロックではなく**常に載る**ため、ここには含めない。
 */
export const DIAGNOSIS_THANKYOU_BLOCK_KINDS = [
    'inspection_soon',
    'keeper_trial',
    'washpass_high',
    'washpass_mid',
    'inspection_unknown',
    'car_sales_hot',
];
/**
 * 条件ブロックの**表示順**（設計書 §4 / **固定**）。
 *
 * > 車検 3 ヶ月以内 ＞ KeePer 体験 ＞ WashPass ＞ その他
 *
 * 2026-09-20 まではこの順で**上位 2 個に絞って 1 通**に載せていた（「1 通で迫る Act は 1 つ」）。
 * 現在は**該当したブロックを全部**・**ブロックごとに別メッセージ**で送る
 * （`planDiagnosisThankYouDelivery` / 運用者指示）ので、ここは順序だけを決める。
 */
export const DIAGNOSIS_THANKYOU_PRIORITY = [
    'inspection_soon',
    'keeper_trial',
    'washpass_high',
    'washpass_mid',
    'inspection_unknown',
    'car_sales_hot',
];
/**
 * 回答 → サンキューの条件ブロック（該当分**すべて**・優先順 / 純関数）。
 *
 * 判定は**選択肢 id** で行う（`interestChoice` ではない）— 同じ確度でも
 * 「週 1 回以上」と「月 1〜2 回」で文面を出し分ける必要があるため（設計書 §4）。
 *
 * ⚠ 上限は設けない。かつての「最大 2 個」は 1 通に連結していた頃の制約で、
 * ブロックごとに別メッセージで送る現在は**該当したクーポンを黙って落とす**だけになる。
 */
export function selectDiagnosisThankYouBlocks(answers) {
    const candidates = [];
    if (answers.inspection === 'within_3m')
        candidates.push('inspection_soon');
    if (answers.inspection === 'unknown')
        candidates.push('inspection_unknown');
    if (answers.keeper === 'interested')
        candidates.push('keeper_trial');
    if (answers.washpass === 'weekly')
        candidates.push('washpass_high');
    if (answers.washpass === 'monthly')
        candidates.push('washpass_mid');
    if (answers.car_sales === 'within_1y')
        candidates.push('car_sales_hot');
    return DIAGNOSIS_THANKYOU_PRIORITY.filter((kind) => candidates.includes(kind));
}
/**
 * 文面を送るブロック（M-133 / **設定が無いときは全部**）。
 *
 * 定義から導出する — 配列を書き写すと、ブロックを増やしたときに新しいブロックだけ
 * 既定で送られないという非対称が黙って生まれる。
 */
export const DEFAULT_ENABLED_THANKYOU_BLOCKS = DIAGNOSIS_THANKYOU_PRIORITY;
/**
 * 実効の「文面を送るブロック」を解決する（**サンキュー本文生成の唯一の入口**）。
 *
 * - `undefined`（未設定）→ コード既定（全ブロック = 現行と同じ挙動）
 * - 配列 → その内容。**空配列は「条件ブロックを 1 つも送らない」という意味を持つ明示値**
 *   （`?? 全部` のような書き方で潰さないこと）
 *
 * 🔴 **これはクーポン付与を制御しない。** 付与枠は `selectDiagnosisCouponSlots` が
 * **ブロック選定**から導いており、そちらは本設定を通さない。ここを兼用すると
 * 「文面を消したらクーポンも黙って止まった」が起きる（設計書 §10.2.2）。
 *
 * 未知のキー（設定 doc に古いブロック名が残っている等）は落とし、**優先順に正規化**して返す。
 */
export function resolveEnabledThankYouBlocks(enabledBlocks) {
    if (enabledBlocks === undefined || enabledBlocks === null) {
        return DEFAULT_ENABLED_THANKYOU_BLOCKS;
    }
    const wanted = new Set(enabledBlocks.filter(isDiagnosisThankYouBlockKind));
    return DIAGNOSIS_THANKYOU_PRIORITY.filter((kind) => wanted.has(kind));
}
/** 文字列が既知の条件ブロック名か（設定 doc の古いキーを落とすために使う）。 */
export function isDiagnosisThankYouBlockKind(value) {
    return DIAGNOSIS_THANKYOU_BLOCK_KINDS.includes(value);
}
// ---------------------------------------------------------------------------
// クーポン（設計書 §4 / §9）
// ---------------------------------------------------------------------------
/**
 * 診断が付与しうるクーポンの枠。**枠であって定義ではない** — 実体は既存クーポンマスタの
 * doc で、設定 doc が持つのは `couponId` 参照のみ（設計書 §10.1-3）。
 */
export const DIAGNOSIS_COUPON_SLOTS = ['fuel', 'inspection', 'keeper'];
/**
 * 条件ブロックに紐づくクーポン枠（無いブロックは undefined）。
 *
 * 配信順（`planDiagnosisThankYouDelivery`）は「そのブロックのクーポン → そのブロックの文面」
 * なので、ブロック → 枠の対応を**ここ 1 箇所**で持つ。
 */
export function diagnosisCouponSlotForBlock(block) {
    if (block === 'inspection_soon')
        return 'inspection';
    if (block === 'keeper_trial')
        return 'keeper';
    return undefined;
}
/**
 * 付与するクーポン枠を決める（純関数）。
 *
 * - `fuel` は**回答者全員**（設計書 §2-2 / インセンティブ）。燃料は D 直行が無く
 *   claim が鮮度アンカーにしかならないため、インセンティブがステージを汚染しない
 * - `inspection` / `keeper` は**対応する条件ブロックが選ばれたときだけ**
 *   （`diagnosisCouponSlotForBlock`）。順序は `blocks` に従う
 */
export function selectDiagnosisCouponSlots(blocks) {
    const slots = ['fuel'];
    for (const block of blocks) {
        const slot = diagnosisCouponSlotForBlock(block);
        if (slot && !slots.includes(slot))
            slots.push(slot);
    }
    return slots;
}
export const DEFAULT_DIAGNOSIS_CYCLE_PARAMS = {
    cycleDays: 90,
    requestionDays: 180,
    newFriendExclusionDays: 30,
    pilotSize: 100,
};
/**
 * ゲート基準（設計書 §5.1 / **表示のみ・固定**）。
 *
 * 判断は人が行う。基準の変更は設計書の改訂を通す（§10.2）。
 */
export const DIAGNOSIS_GATE_HIGH_PERCENT = 15;
export const DIAGNOSIS_GATE_LOW_PERCENT = 8;
/**
 * 先行配信の回答率からゲート判定を出す（純関数 / 表示用）。
 *
 * - 15% 以上 → `go`（残り全員へ同一文面）
 * - 8% 以上 15% 未満 → `adjust`（文面・訴求を調整して残り全員へ）
 * - 8% 未満 → `stop`（配信を止め設計に戻す）
 * - 母数 0 → `pending`（判定不能）
 */
export function evaluateDiagnosisGate(responded, delivered) {
    if (delivered <= 0)
        return { verdict: 'pending', ratePercent: null };
    const ratePercent = (responded / delivered) * 100;
    if (ratePercent >= DIAGNOSIS_GATE_HIGH_PERCENT)
        return { verdict: 'go', ratePercent };
    if (ratePercent >= DIAGNOSIS_GATE_LOW_PERCENT)
        return { verdict: 'adjust', ratePercent };
    return { verdict: 'stop', ratePercent };
}
// ---------------------------------------------------------------------------
// 無作為抽出（設計書 §5.1 / 要件 6「再現可能な seed 付き」）
// ---------------------------------------------------------------------------
/** 32bit 決定論 PRNG（mulberry32）。seed が同じなら同じ系列を返す。 */
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** 文字列 seed → 32bit 整数（FNV-1a）。人が読める seed を使えるようにするため。 */
export function diagnosisSeedToInt(seed) {
    let h = 0x811c9dc5;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * 無作為抽出（決定論 / 純関数）。
 *
 * ⚠ **seed だけでは再現できない**。同じ seed でも母集団（`userIds`）が変われば別の集合に
 * なるため、呼出元は**抽出結果そのもの**（`sampledUserIds`）もサイクル doc に保存すること。
 * seed は「同じ入力なら同じ出力」の検証用であって、履歴の代替ではない。
 *
 * 入力順に依存しないよう**先にソート**してから Fisher-Yates で並べ替える
 * （Firestore の取得順は保証されないため、ソートしないと seed が意味を持たない）。
 */
export function sampleUserIdsWithSeed(userIds, size, seed) {
    if (size <= 0)
        return [];
    const pool = [...new Set(userIds)].sort();
    if (pool.length <= size)
        return pool;
    const rand = mulberry32(diagnosisSeedToInt(seed));
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = pool[i];
        pool[i] = pool[j];
        pool[j] = tmp;
    }
    return pool.slice(0, size).sort();
}
function daysBetween(fromIso, toIso) {
    const from = Date.parse(fromIso);
    const to = Date.parse(toIso);
    if (Number.isNaN(from) || Number.isNaN(to))
        return null;
    return (to - from) / 86_400_000;
}
/**
 * 定期サイクルの対象を決める（純関数 / 設計書 §5.3）。
 *
 * 対象 = 全友だち − ブロック − 直近 `requestionDays` 日以内の回答者 − 登録 `newFriendExclusionDays` 日未満。
 *
 * ⚠ **これは抽出であって配信の可否判定ではない**。`lineBlocked` 以外の抑制
 * （`isInterestOptedOut` / HOT 商談中）は配信側の `matchesSegment` が見るので、
 * ここで重複して落とさない（落とすと「なぜ届かないか」の説明が 2 箇所に分裂する）。
 *
 * 解釈不能な日付（parse 不能）は**除外しない**方向に倒す（配信の取りこぼしより、
 * 送ってしまう方が回復可能なため）。
 */
export function resolveDiagnosisTargets(candidates, params, nowIso) {
    const targets = [];
    const excluded = [];
    for (const c of candidates) {
        if (c.lineBlocked === true) {
            excluded.push({ uid: c.uid, reason: 'blocked' });
            continue;
        }
        if (!c.lineUserId) {
            excluded.push({ uid: c.uid, reason: 'no_line_user_id' });
            continue;
        }
        if (c.lastDiagnosisAnsweredAt) {
            const elapsed = daysBetween(c.lastDiagnosisAnsweredAt, nowIso);
            if (elapsed !== null && elapsed < params.requestionDays) {
                excluded.push({ uid: c.uid, reason: 'recently_answered' });
                continue;
            }
        }
        if (c.followedAt) {
            const elapsed = daysBetween(c.followedAt, nowIso);
            if (elapsed !== null && elapsed < params.newFriendExclusionDays) {
                excluded.push({ uid: c.uid, reason: 'new_friend' });
                continue;
            }
        }
        targets.push(c.uid);
    }
    return { targets: targets.sort(), excluded };
}
/**
 * リマインド対象（設計書 §7-7 / 純関数）。
 *
 * 招待 campaignId 到達 − `survey_answer` 保持者 − ブロック。
 *
 * 「到達」は**招待を実際に送れた宛先**（`deliveredUserIds`）で表す。promo_events の
 * クエリではなくサイクル doc の記録を使うのは、リマンド母集団が配信記録と
 * 1 対 1 で対応していないと「送っていない人にリマインドする」が起きるため。
 */
export function resolveDiagnosisRemindTargets(deliveredUserIds, respondedUserIds, blockedUserIds) {
    const responded = new Set(respondedUserIds);
    const blocked = new Set(blockedUserIds);
    return [...new Set(deliveredUserIds)]
        .filter((uid) => !responded.has(uid) && !blocked.has(uid))
        .sort();
}
export const EMPTY_DIAGNOSIS_CYCLE_COUNTS = {
    delivered: 0,
    reached: 0,
    responded: 0,
    dueMonthProvided: 0,
};
export function computeDiagnosisKdi(counts) {
    const ratio = (n, d) => (d > 0 ? n / d : null);
    return {
        responseRate: ratio(counts.responded, counts.delivered),
        formCompletionRate: ratio(counts.responded, counts.reached),
        dueMonthRate: ratio(counts.dueMonthProvided, counts.responded),
    };
}
//# sourceMappingURL=promoDiagnosis.js.map